<template>
  <div class="cqw">
    <div class="cqw-bar">
      <div class="cqw-tabs" role="tablist">
        <button
          type="button"
          class="cqw-tab"
          :class="{ 'cqw-tab--on': tab === 'pending' }"
          role="tab"
          :aria-selected="tab === 'pending'"
          @click="setTab('pending')"
        >
          {{ t("pending") }}
          <span v-if="loaded" class="cqw-count" :class="{ 'cqw-count--alert': pending.length }">{{ pending.length }}</span>
        </button>
        <button
          type="button"
          class="cqw-tab"
          :class="{ 'cqw-tab--on': tab === 'answered' }"
          role="tab"
          :aria-selected="tab === 'answered'"
          @click="setTab('answered')"
        >
          {{ t("answered") }}
          <span v-if="loaded" class="cqw-count" :class="{ 'cqw-count--new': canManage && unread }">{{ answered.length }}</span>
        </button>
        <!-- Historial de las ya resueltas: chico, al costado. -->
        <button
          v-if="archived.length || tab === 'archived'"
          type="button"
          class="cqw-tab cqw-tab--small"
          :class="{ 'cqw-tab--on': tab === 'archived' }"
          role="tab"
          :aria-selected="tab === 'archived'"
          :title="t('archived')"
          @click="setTab('archived')"
        >
          <i class="fas fa-archive" aria-hidden="true"></i> {{ t("archived") }}
          <span class="cqw-count">{{ archived.length }}</span>
        </button>
      </div>
      <button v-if="canManage && !form" type="button" class="btn btn-sm btn-outline-primary cqw-new" @click="openForm()">
        <i class="fas fa-plus" aria-hidden="true"></i> {{ t("new") }}
      </button>
    </div>

    <!-- Alta / edición: solo nosotros (admin o programadores). -->
    <form v-if="form" class="cqw-form" @submit.prevent="saveForm">
      <input v-model="form.topic" class="form-control form-control-sm" maxlength="120" :placeholder="t('topicPlaceholder')" />
      <textarea v-model="form.question" class="form-control form-control-sm" rows="2" maxlength="2000" :placeholder="t('questionPlaceholder')" required></textarea>
      <textarea v-model="form.context" class="form-control form-control-sm" rows="3" maxlength="6000" :placeholder="t('contextPlaceholder')"></textarea>
      <div class="cqw-actions">
        <button type="button" class="btn btn-sm btn-link" @click="form = null">{{ t("cancel") }}</button>
        <button type="submit" class="btn btn-sm btn-primary" :disabled="saving || !form.question.trim()">{{ t("save") }}</button>
      </div>
    </form>

    <ContentSkeleton v-if="loading && !loaded" variant="list" :rows="3" />
    <p v-else-if="error" class="dw-empty">{{ $t("dashboardWidgets.loadError") }}</p>
    <p v-else-if="!shown.length" class="dw-empty">{{ emptyText }}</p>
    <ul v-else class="cqw-list">
      <li
        v-for="q in shown"
        :key="q._id"
        class="cqw-item"
        :class="{ 'cqw-item--new': canManage && q.status === 'answered' && !q.answerReadAt, 'cqw-item--archived': q.status === 'archived' }"
      >
        <div class="cqw-head">
          <span v-if="q.topic" class="cqw-topic">{{ q.topic }}</span>
          <span v-if="canManage && q.status === 'answered' && !q.answerReadAt" class="cqw-badge">{{ t("newAnswer") }}</span>
          <span class="cqw-spacer"></span>
          <template v-if="canManage && q.status !== 'archived'">
            <button type="button" class="cqw-icon" :title="t('edit')" @click="openForm(q)"><i class="fas fa-pen"></i></button>
            <button type="button" class="cqw-icon cqw-icon--danger" :title="t('delete')" @click="remove(q)"><i class="fas fa-trash-alt"></i></button>
          </template>
        </div>
        <p class="cqw-question">{{ q.question }}</p>
        <p v-if="q.context" class="cqw-context">
          <template v-for="(part, pi) in linkParts(q.context)">
            <a
              v-if="part.url"
              :key="pi"
              :href="part.url"
              :target="part.internal ? null : '_blank'"
              :rel="part.internal ? null : 'noopener noreferrer'"
              @click="openLink($event, part)"
            >{{ part.text }}</a>
            <template v-else>{{ part.text }}</template>
          </template>
        </p>

        <div v-if="q.status !== 'pending' && editing !== q._id" class="cqw-answer">
          <p class="cqw-answer__text">{{ q.answer }}</p>
          <span class="cqw-meta">{{ t("answeredBy", { name: q.answeredByName || "—", date: when(q.answeredAt) }) }}</span>
          <span v-if="q.status === 'archived'" class="cqw-meta cqw-meta--block">
            {{ t("archivedBy", { name: q.archivedByName || "—", date: when(q.archivedAt) }) }}
          </span>
          <details v-if="q.previousAnswers && q.previousAnswers.length" class="cqw-prev">
            <summary>{{ t("previousAnswers", { n: q.previousAnswers.length }) }}</summary>
            <p v-for="(p, idx) in q.previousAnswers" :key="idx" class="cqw-prev__item">
              {{ p.answer }} <span class="cqw-meta">— {{ p.answeredByName }}, {{ when(p.answeredAt) }}</span>
            </p>
          </details>
        </div>

        <div v-if="editing === q._id" class="cqw-reply">
          <textarea
            ref="reply"
            v-model="draft"
            class="form-control form-control-sm"
            rows="3"
            maxlength="6000"
            :placeholder="t('answerPlaceholder')"
            @mousedown.stop
            @touchstart.stop
          ></textarea>
          <div class="cqw-actions">
            <button type="button" class="btn btn-sm btn-link" @click="cancelReply">{{ t("cancel") }}</button>
            <button type="button" class="btn btn-sm btn-primary" :disabled="saving || !draft.trim()" @click="sendAnswer(q)">
              {{ t("send") }}
            </button>
          </div>
        </div>
        <div v-else-if="q.status === 'archived'" class="cqw-actions">
          <button v-if="canManage" type="button" class="btn btn-sm btn-link" @click="unarchive(q)">{{ t("unarchive") }}</button>
        </div>
        <div v-else class="cqw-actions">
          <button v-if="canManage && q.status === 'answered' && !q.answerReadAt" type="button" class="btn btn-sm btn-link" @click="markRead(q)">
            {{ t("markRead") }}
          </button>
          <button v-if="canManage && q.status === 'answered'" type="button" class="btn btn-sm btn-link" @click="reopen(q)">
            {{ t("reopen") }}
          </button>
          <button v-if="canManage && q.status === 'answered'" type="button" class="btn btn-sm btn-outline-secondary" @click="archive(q)">
            <i class="fas fa-archive" aria-hidden="true"></i> {{ t("archive") }}
          </button>
          <button v-if="q.status === 'pending'" type="button" class="btn btn-sm btn-primary" @click="startReply(q)">
            <i class="fas fa-reply" aria-hidden="true"></i> {{ t("reply") }}
          </button>
          <button v-else type="button" class="btn btn-sm btn-outline-secondary" @click="startReply(q)">{{ t("correct") }}</button>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
/**
 * Dudas para el cliente sobre lo que vamos desarrollando (back: clientQuestionController).
 * El cliente las ve y las contesta acá; nosotros (admin o programadores de soporte) las
 * cargamos y leemos las respuestas en la pestaña «Respondidas», donde las nuevas quedan
 * marcadas hasta que las damos por leídas. Las que ya resolvimos se archivan: pasan a
 * «Archivadas», un historial de solo lectura que no cuenta en «Respondidas».
 */
import Swal from "sweetalert2";
import { wencesApi } from "@/api/wencesApi";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import { formatTimestampSync } from "@/utils/dateFormatter";

export default {
  name: "ClientQuestionsWidget",
  components: { ContentSkeleton },
  props: {
    entry: { type: Object, required: true },
    config: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loading: false,
      loaded: false,
      error: false,
      items: [],
      canManage: false,
      tab: ["answered", "archived"].includes(this.config.tab) ? this.config.tab : "pending",
      editing: null,
      draft: "",
      form: null,
      saving: false,
    };
  },
  computed: {
    // Pendientes: las más viejas primero (son las que más esperan); respondidas: la última arriba.
    pending() {
      return this.items.filter((q) => q.status === "pending").sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
    },
    answered() {
      return this.items.filter((q) => q.status === "answered").sort((a, b) => String(b.answeredAt).localeCompare(String(a.answeredAt)));
    },
    // Archivadas: historial de las ya resueltas, la última archivada arriba.
    archived() {
      return this.items.filter((q) => q.status === "archived").sort((a, b) => String(b.archivedAt).localeCompare(String(a.archivedAt)));
    },
    unread() {
      return this.answered.filter((q) => !q.answerReadAt).length;
    },
    shown() {
      return this[this.tab] || this.pending;
    },
    emptyText() {
      if (this.tab === "archived") return this.t("emptyArchived");
      return this.tab === "pending" ? this.t("emptyPending") : this.t("emptyAnswered");
    },
  },
  mounted() {
    this.$emit("menu", []);
    this.load();
  },
  methods: {
    t(key, params) {
      return this.$t(`dashboardWidgets.clientQuestions.${key}`, params);
    },
    /**
     * Parte el texto en trozos y links para dibujarlos sin v-html: `[texto](https://...)`
     * muestra solo el texto; una URL suelta, la URL. Los links al mismo sistema (ejemplos
     * de contratos) se abren con el router, sin recargar.
     */
    linkParts(text) {
      const parts = [];
      const re = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)|https?:\/\/[^\s<>()]+[^\s<>().,;:!?»"']/g;
      let last = 0;
      let m;
      while ((m = re.exec(text))) {
        if (m.index > last) parts.push({ text: text.slice(last, m.index) });
        const url = m[2] || m[0];
        let internal = false;
        let path = "";
        try {
          const u = new URL(url);
          internal = u.host === window.location.host;
          path = u.pathname + u.search + u.hash;
        } catch (e) {
          internal = false;
        }
        parts.push({ text: m[1] || url, url, internal, path });
        last = m.index + m[0].length;
      }
      if (last < text.length) parts.push({ text: text.slice(last) });
      return parts;
    },
    openLink(e, part) {
      if (!part.internal || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      this.$router.push(part.path).catch(() => {});
    },
    when(at) {
      return at ? formatTimestampSync(at) : "";
    },
    async load() {
      this.loading = true;
      this.error = false;
      try {
        const { data } = await wencesApi.get("/api/client-questions", { params: { status: "all", _: Date.now() } });
        this.items = (data && data.data) || [];
        this.canManage = !!(data && data.canManage);
        this.loaded = true;
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    replace(doc) {
      const idx = this.items.findIndex((q) => q._id === doc._id);
      if (idx >= 0) this.items.splice(idx, 1, doc);
      else this.items.push(doc);
    },
    setTab(tab) {
      this.tab = tab;
      this.cancelReply();
      this.$emit("update-config", { tab });
    },
    startReply(q) {
      this.editing = q._id;
      this.draft = q.status === "answered" ? q.answer || "" : "";
      this.$nextTick(() => {
        const el = Array.isArray(this.$refs.reply) ? this.$refs.reply[0] : this.$refs.reply;
        if (el) el.focus();
      });
    },
    cancelReply() {
      this.editing = null;
      this.draft = "";
    },
    async sendAnswer(q) {
      this.saving = true;
      try {
        const { data } = await wencesApi.post(`/api/client-questions/${q._id}/answer`, { answer: this.draft });
        this.replace(data.data);
        this.cancelReply();
        Swal.fire({ icon: "success", title: this.t("thanks"), timer: 1600, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: "error", title: this.t("saveError") });
      } finally {
        this.saving = false;
      }
    },
    async archive(q) {
      try {
        const { data } = await wencesApi.post(`/api/client-questions/${q._id}/archive`);
        this.replace(data.data);
      } catch (e) {
        Swal.fire({ icon: "error", title: this.t("saveError") });
      }
    },
    async unarchive(q) {
      try {
        const { data } = await wencesApi.post(`/api/client-questions/${q._id}/unarchive`);
        this.replace(data.data);
      } catch (e) {
        Swal.fire({ icon: "error", title: this.t("saveError") });
      }
    },
    async markRead(q) {
      try {
        const { data } = await wencesApi.post(`/api/client-questions/${q._id}/read`);
        this.replace(data.data);
      } catch (e) {
        Swal.fire({ icon: "error", title: this.t("saveError") });
      }
    },
    async reopen(q) {
      try {
        const { data } = await wencesApi.put(`/api/client-questions/${q._id}`, { reopen: true });
        this.replace(data.data);
      } catch (e) {
        Swal.fire({ icon: "error", title: this.t("saveError") });
      }
    },
    openForm(q = null) {
      this.form = q
        ? { _id: q._id, question: q.question, context: q.context || "", topic: q.topic || "" }
        : { question: "", context: "", topic: "" };
    },
    async saveForm() {
      const f = this.form;
      if (!f || !f.question.trim()) return;
      this.saving = true;
      try {
        const body = { question: f.question, context: f.context, topic: f.topic };
        const { data } = f._id
          ? await wencesApi.put(`/api/client-questions/${f._id}`, body)
          : await wencesApi.post("/api/client-questions", body);
        this.replace(data.data);
        this.form = null;
        if (!f._id) this.tab = "pending";
      } catch (e) {
        Swal.fire({ icon: "error", title: this.t("saveError") });
      } finally {
        this.saving = false;
      }
    },
    async remove(q) {
      const { isConfirmed } = await Swal.fire({
        icon: "warning",
        title: this.t("deleteConfirm"),
        showCancelButton: true,
        confirmButtonText: this.t("delete"),
        cancelButtonText: this.t("cancel"),
      });
      if (!isConfirmed) return;
      try {
        await wencesApi.delete(`/api/client-questions/${q._id}`);
        this.items = this.items.filter((x) => x._id !== q._id);
      } catch (e) {
        Swal.fire({ icon: "error", title: this.t("saveError") });
      }
    },
    onMenu(key) {
      if (key === "refresh") this.load();
    },
  },
};
</script>

<style scoped>
.cqw { display: flex; flex-direction: column; gap: 8px; }
.cqw-bar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; position: sticky; top: 0; z-index: 1; padding-bottom: 4px; background: var(--dw-surface); }
.cqw-tabs { display: inline-flex; gap: 2px; padding: 2px; border-radius: 8px; background: var(--dw-surface-2); }
.cqw-tab {
  border: 0;
  background: transparent;
  color: var(--dw-muted);
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.cqw-tab--on { background: var(--dw-surface); color: var(--dw-text); box-shadow: var(--dw-shadow); }
.cqw-count { font-size: 0.7rem; padding: 0 6px; border-radius: 999px; background: var(--dw-line); color: var(--dw-text); }
.cqw-count--alert { background: var(--bs-warning); color: var(--bs-dark); }
.cqw-count--new { background: var(--bs-primary); color: var(--bs-white); }
.cqw-new { margin-left: auto; }
.cqw-form { display: flex; flex-direction: column; gap: 6px; padding: 8px; border: 1px solid var(--dw-border); border-radius: 8px; }
.cqw-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.cqw-item {
  padding: 10px 12px;
  border: 1px solid var(--dw-border);
  border-left: 3px solid var(--bs-warning);
  border-radius: 8px;
  background: var(--dw-surface);
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.cqw-item--new { border-left-color: var(--bs-primary); }
.cqw-item--archived { border-left-color: var(--dw-border); }
.cqw-tab--small { font-size: 0.72rem; font-weight: 500; padding: 3px 8px; }
.cqw-meta--block { display: block; }
.cqw-head { display: flex; align-items: center; gap: 6px; min-height: 18px; }
.cqw-spacer { flex: 1; }
.cqw-topic { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--dw-muted); font-weight: 600; }
.cqw-badge { font-size: 0.68rem; padding: 0 6px; border-radius: 999px; background: rgba(var(--bs-primary-rgb), 0.14); color: var(--bs-primary); font-weight: 600; }
.cqw-icon { border: 0; background: transparent; color: var(--dw-muted); font-size: 0.75rem; padding: 2px 4px; }
.cqw-icon:hover { color: var(--bs-primary); }
.cqw-icon--danger:hover { color: var(--bs-danger); }
.cqw-question { margin: 0; font-weight: 600; font-size: 0.9rem; color: var(--dw-text); white-space: pre-wrap; }
.cqw-context { margin: 0; font-size: 0.8rem; color: var(--dw-muted); white-space: pre-wrap; overflow-wrap: anywhere; }
.cqw-context a { color: var(--bs-primary); }
.cqw-answer { padding: 8px 10px; border-radius: 6px; background: rgba(var(--bs-success-rgb), 0.08); border: 1px solid rgba(var(--bs-success-rgb), 0.25); }
.cqw-answer__text { margin: 0 0 4px; font-size: 0.85rem; color: var(--dw-text); white-space: pre-wrap; }
.cqw-meta { font-size: 0.72rem; color: var(--dw-muted); }
.cqw-prev { margin-top: 4px; font-size: 0.75rem; color: var(--dw-muted); }
.cqw-prev__item { margin: 4px 0 0; white-space: pre-wrap; }
.cqw-reply { display: flex; flex-direction: column; gap: 6px; }
.cqw-actions { display: flex; justify-content: flex-end; align-items: center; gap: 6px; flex-wrap: wrap; }
</style>
